/**
 * /es/ia-para-tomar-notas-de-reuniones — AI meeting-notes reframe bridge (ES).
 *
 * Targets ia para tomar notas de reuniones (gratis) / transcribir reuniones de
 * meet. Most on-target ES bridge: keep your notetaker, capture the decision.
 */
import { MarketingShell } from './MarketingShell';

export function EsNotasReuniones() {
  return (
    <MarketingShell
      currentLocale="es"
      hreflangPath="/ia-para-tomar-notas-de-reuniones"
      hreflangLocales={['es']}
      documentTitle="IA para tomar notas de reuniones (gratis): guía honesta · Draftlize"
      documentDescription="Las mejores formas de tomar notas de reuniones con IA gratis — y por qué la nota no es el objetivo: lo que importa es la decisión que queda dentro."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guía · Reuniones</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            IA para notas de reuniones<br />
            <em>— y lo que de verdad importa después.</em>
          </h1>
          <p className="lede">
            Terminas la llamada y aparece un resumen ordenado. Las herramientas de IA para tomar notas — Otter, Fireflies, Notion AI, Read — son geniales capturando la conversación, y muchas tienen plan gratuito. Pero una nota de reunión es <em>materia prima</em>, no el resultado. Lo que importaba — la <em>decisión</em> que tomasteis y por qué — queda enterrado en un muro de notas, aterriza en un doc que nadie reabre y caduca en semanas. Esta página trata del paso después del bloc de notas.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">La IA ya toma las notas. <em>El problema es lo que sigue.</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            Tomar notas con IA (y <a href="/es/transcribir-entrevista" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>transcribir</a> reuniones de Meet o Zoom) ya es un problema resuelto. El que queda: la decisión — lo que de verdad importa — se queda como texto plano, desconectado del spec que afecta y de la decisión anterior que revierte. Así caduca en silencio, y la siguiente reunión vuelve a decidir lo ya acordado.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Nota vs decisión</span>
            <h2 className="serif">Quédate con tu herramienta de notas. <em>Captura la decisión.</em></h2>
          </div>
          <p>Tu app de notas y Draftlize hacen trabajos distintos. Una captura la reunión; la otra mantiene viva la decisión que salió de ella.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>Notas con IA</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Función</td><td>Capturar lo dicho</td><td className="ours yes">Mantener al día lo decidido</td></tr>
            <tr><td>Salida</td><td className="partial">Resumen + acta</td><td className="ours yes">Tarjetas de decisión enlazadas</td></tr>
            <tr><td>Una decisión revierte otra</td><td className="no">Dos notas sueltas</td><td className="ours yes">Enlazadas; la antigua se marca «stale»</td></tr>
            <tr><td>«Acción» vs decisión</td><td className="partial">Tarea que se tacha</td><td className="ours yes">Decisión con motivo y vigencia</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Tu herramienta captura la reunión perfectamente. Solo que no puede decirte, tres semanas después, cuál de las decisiones de dentro sigue siendo cierta.
        </blockquote>
        <cite>Quédate con el bloc de notas. Deja que Draftlize mantenga vivas las decisiones.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Preguntas frecuentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '¿Qué IA toma notas de reuniones gratis?', a: 'Varias herramientas (Otter, Fireflies, Notion AI, Read) tienen plan gratuito. Draftlize no es un tomanotas: convierte las decisiones de esas notas en tarjetas que se mantienen al día.' },
            { q: '¿Funciona con Google Meet o Zoom?', a: 'Tu herramienta de notas se encarga de la captura en Meet/Zoom. Después, pega el resumen en Draftlize para capturar las decisiones como tarjetas con seguimiento.' },
            { q: '¿Cómo hago seguimiento de los acuerdos de una reunión?', a: 'Guarda cada acuerdo como una tarjeta de decisión con su motivo y responsable. Si una decisión posterior lo revierte, lo que depende de él se marca «stale» automáticamente.' },
            { q: '¿En qué se diferencia de los «action items»?', a: 'Una acción es una tarea que se tacha. Una decisión tiene motivo, dependencias y vigencia, y caduca cuando algo aguas arriba cambia. Draftlize rastrea eso; una lista de tareas no.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Convierte cada acuerdo en una tarjeta con seguimiento <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Regístrate y recibe $5</span>
          <span>Paga solo por lo que usas</span>
          <span>El saldo nunca caduca</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Quédate con tu herramienta de notas. Suelta el resumen en Draftlize y cada decisión de la reunión se vuelve una tarjeta viva — enlazada, al día y legible por tu agente de IA.
          </p>
          <a className="btn-primary" href="/">Empieza gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
